import {
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Loader2,
    Search,
    UserMinus,
    Users,
    X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Typogaphy } from 'vnetwork-ui'
import useDebounce from '@/hooks/useDebounce'
import { CustomerPreview } from '@/hooks/useCustomersPreviewFilter'
import { Pagination } from '@/types/shared'

interface CustomerPreviewTableProps {
    customers: CustomerPreview[]
    pagination?: Pagination
    isLoading?: boolean
    excludedCustomerIds?: number[]
    onToggleExcludeCustomer?: (customerId: number) => void
    /** Already surveyed this quarter: listed and flagged. Selectable one by one
     *  (picking one updates that record), but deliberately excluded from
     *  "select all" so a bulk action cannot overwrite existing answers. */
    surveyedCustomerIds?: number[]
    /** Badge text for those rows. Owned by the caller so this shared table
     *  stays free of domain-specific translations. */
    surveyedLabel?: string
    onPageChange?: (page: number) => void
    onSearch?: (search: string) => void
    showExcludeToggle?: boolean
    showSearch?: boolean
    maxHeight?: string
    customerIdField?: string
    selectionMode?: 'include' | 'exclude'
    selectedCustomerIds?: number[]
    onToggleSelectCustomer?: (customerId: number) => void
    onToggleSelectAll?: (customerIds: number[], isSelected: boolean) => void
}

const CustomerPreviewTable: React.FC<CustomerPreviewTableProps> = ({
    customers = [],
    pagination,
    isLoading = false,
    excludedCustomerIds = [],
    onToggleExcludeCustomer,
    surveyedCustomerIds = [],
    surveyedLabel,
    selectedCustomerIds = [],
    onToggleSelectCustomer,
    onToggleSelectAll,
    onPageChange,
    onSearch,
    showExcludeToggle = false,
    showSearch = true,
    maxHeight = '300px',
    customerIdField = 'customer_id',
    selectionMode = 'exclude',
}) => {
    const { t } = useTranslation()
    const [searchValue, setSearchValue] = useState('')
    const [pageInput, setPageInput] = useState('')
    const debouncedSearch = useDebounce(searchValue, 500)

    const currentPage = pagination?.page || 1
    const pageSize = pagination?.page_size || 10
    const total = pagination?.total || 0
    const totalPages = pagination?.total_pages || 0

    // Auto search when debounced value changes
    useEffect(() => {
        if (onSearch && debouncedSearch !== undefined) {
            onSearch(debouncedSearch)
        }
    }, [debouncedSearch, onSearch])

    // Handle clear search
    const handleClearSearch = () => {
        setSearchValue('')
    }

    // Handle page input
    const handlePageInputKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (e.key === 'Enter') {
            const page = parseInt(pageInput, 10)
            if (page >= 1 && page <= totalPages && onPageChange) {
                onPageChange(page)
                setPageInput('')
            }
        }
    }

    // Calculate effective total
    const effectiveTotal =
        selectionMode === 'include'
            ? selectedCustomerIds.length
            : total - excludedCustomerIds.length

    // Get customer ID from customer object
    const getCustomerId = (customer: CustomerPreview): number | undefined => {
        const id = customer[customerIdField]
        return typeof id === 'number' ? id : undefined
    }

    const isSurveyedCustomer = (id?: number) =>
        id !== undefined && surveyedCustomerIds.includes(id)

    // Calculate selection state for current page. Surveyed customers are shown but
    // never selectable, so they stay out of "select all" and its checked state too.
    const currentPageIds = customers
        .map(getCustomerId)
        .filter((id): id is number => id !== undefined)
        .filter((id) => !isSurveyedCustomer(id))

    const areAllSelected =
        currentPageIds.length > 0 &&
        currentPageIds.every((id) => selectedCustomerIds.includes(id))

    return (
        <div className="border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gray-50 px-3 py-2 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Users size={14} className="text-green-600" />
                    <Typogaphy className="text-sm font-medium text-gray-700">
                        {t('preview_customers')}
                    </Typogaphy>
                </div>
                <div className="flex items-center gap-3">
                    {/* Count badges */}
                    {showExcludeToggle &&
                        selectionMode === 'exclude' &&
                        excludedCustomerIds.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                                <UserMinus size={12} />
                                {t('excluded')}: {excludedCustomerIds.length}
                            </span>
                        )}
                    {selectionMode === 'include' &&
                        selectedCustomerIds.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                <Users size={12} />
                                {t('selected')}: {selectedCustomerIds.length}
                            </span>
                        )}
                    {surveyedCustomerIds.length > 0 && surveyedLabel && (
                        <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                            <CheckCircle2 size={12} />
                            {surveyedLabel}: {surveyedCustomerIds.length}
                        </span>
                    )}

                    {pagination && selectionMode === 'exclude' && (
                        <span className="text-xs text-gray-500">
                            {t('total')}: <strong>{effectiveTotal}</strong>{' '}
                            {t('customers')}
                            {showExcludeToggle &&
                                excludedCustomerIds.length > 0 && (
                                    <span className="text-gray-400 ml-1">
                                        ({total} - {excludedCustomerIds.length})
                                    </span>
                                )}
                        </span>
                    )}
                </div>
            </div>

            {/* Search Bar */}
            {showSearch && onSearch && (
                <div className="px-3 py-2 border-b bg-white">
                    <div className="relative max-w-xs">
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={t('search_by_email_name')}
                            className="w-full pl-8 pr-8 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <Search
                            size={14}
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        {searchValue && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Table Content */}
            {!customers.length && isLoading ? (
                <div className="flex items-center justify-center py-6">
                    <Loader2 className="animate-spin text-primary" size={20} />
                </div>
            ) : customers.length > 0 ? (
                <>
                    <div
                        className="overflow-auto relative"
                        style={{ maxHeight }}
                    >
                        {/* Loading overlay */}
                        {isLoading && (
                            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
                                <Loader2
                                    className="animate-spin text-primary"
                                    size={20}
                                />
                            </div>
                        )}
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr className="z-20">
                                    {(showExcludeToggle ||
                                        selectionMode === 'include') && (
                                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600 w-10">
                                            {selectionMode === 'include' ? (
                                                <div className="flex justify-center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={areAllSelected}
                                                        onChange={() =>
                                                            onToggleSelectAll?.(
                                                                currentPageIds,
                                                                !areAllSelected
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <Typogaphy className="text-[10px] text-gray-500">
                                                    {t('include')}
                                                </Typogaphy>
                                            )}
                                        </th>
                                    )}
                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600 w-8">
                                        #
                                    </th>
                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                        Email
                                    </th>
                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                        {t('name')}
                                    </th>
                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                        {t('company')}
                                    </th>
                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                        {t('phone')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {customers.map((customer, idx) => {
                                    const customerId = getCustomerId(customer)
                                    const isExcluded =
                                        selectionMode === 'exclude' &&
                                        customerId &&
                                        excludedCustomerIds.includes(customerId)

                                    const isSelected =
                                        selectionMode === 'include' &&
                                        customerId &&
                                        selectedCustomerIds.includes(customerId)

                                    const isSurveyed =
                                        isSurveyedCustomer(customerId)

                                    const rowNum =
                                        (currentPage - 1) * pageSize + idx + 1

                                    return (
                                        <tr
                                            key={
                                                customerId ||
                                                customer.customer_email ||
                                                idx
                                            }
                                            className={`${
                                                isSurveyed
                                                    ? 'bg-slate-100 opacity-70'
                                                    : isExcluded
                                                      ? 'bg-red-50 opacity-60'
                                                      : isSelected
                                                        ? 'bg-blue-50'
                                                        : idx % 2 === 0
                                                          ? 'bg-white'
                                                          : 'bg-gray-50'
                                            }`}
                                        >
                                            {(showExcludeToggle ||
                                                selectionMode ===
                                                    'include') && (
                                                <td className="px-2 py-1.5 text-center">
                                                    {customerId && (
                                                        <Checkbox
                                                            size="small"
                                                            checked={
                                                                selectionMode ===
                                                                'include'
                                                                    ? !!isSelected
                                                                    : !isExcluded
                                                            }
                                                            onChange={() => {
                                                                if (
                                                                    selectionMode ===
                                                                    'include'
                                                                ) {
                                                                    onToggleSelectCustomer?.(
                                                                        customerId
                                                                    )
                                                                } else {
                                                                    onToggleExcludeCustomer?.(
                                                                        customerId
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </td>
                                            )}
                                            <td className="px-3 py-1.5 text-xs text-gray-500">
                                                {rowNum}
                                            </td>
                                            <td
                                                className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}
                                            >
                                                {customer.customer_email}
                                            </td>
                                            <td
                                                className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-600'}`}
                                            >
                                                <span
                                                    className={
                                                        isSurveyed
                                                            ? 'text-gray-400'
                                                            : undefined
                                                    }
                                                >
                                                    {customer.customer_name ||
                                                        '-'}
                                                </span>
                                                {isSurveyed &&
                                                    surveyedLabel && (
                                                        <span className="ml-2 whitespace-nowrap rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                                                            {surveyedLabel}
                                                        </span>
                                                    )}
                                            </td>
                                            <td
                                                className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-600'}`}
                                            >
                                                {customer.company_name || '-'}
                                            </td>
                                            <td
                                                className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-600'}`}
                                            >
                                                {customer.customer_phone || '-'}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && onPageChange && (
                        <div className="px-3 py-2 bg-gray-50 border-t flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                                {t('page')} {currentPage} / {totalPages} (
                                {total} {t('records')})
                            </span>
                            <div className="flex items-center gap-2">
                                {/* Page input */}
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">
                                        {t('go_to')}:
                                    </span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={totalPages}
                                        value={pageInput}
                                        onChange={(e) =>
                                            setPageInput(e.target.value)
                                        }
                                        onKeyDown={handlePageInputKeyDown}
                                        placeholder={String(currentPage)}
                                        className="w-14 px-2 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                                    />
                                </div>

                                {/* Navigation buttons */}
                                <div className="flex items-center gap-0.5">
                                    <button
                                        type="button"
                                        onClick={() => onPageChange(1)}
                                        disabled={currentPage <= 1 || isLoading}
                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={t('first_page')}
                                    >
                                        <ChevronsLeft size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onPageChange(currentPage - 1)
                                        }
                                        disabled={currentPage <= 1 || isLoading}
                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={t('previous_page')}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onPageChange(currentPage + 1)
                                        }
                                        disabled={
                                            currentPage >= totalPages ||
                                            isLoading
                                        }
                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={t('next_page')}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onPageChange(totalPages)}
                                        disabled={
                                            currentPage >= totalPages ||
                                            isLoading
                                        }
                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={t('last_page')}
                                    >
                                        <ChevronsRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="py-6 text-center text-gray-500 text-sm">
                    {t('no_customers_found')}
                </div>
            )}
        </div>
    )
}

export default CustomerPreviewTable
